import DatetimeRangePicker from "@/common/components/atoms/datetimeRangePicker/datetimeRangePicker";
import { EvesTextbox } from "@/common/components/atoms/textbox/textbox";
import FilterSelector from "@/common/components/filterSelector";
import LookupComponent from "@/common/components/lookupComponent";
import ButtonGroup from "@/common/components/molecules/buttonGroup/buttonGroup";
import ErrorDetailModal from "./components/ErrorDetailModal";
import RetryModal from "./components/RetryModal";
import NotesCell from "./components/NotesCell";
import { ITableActionMenuModel } from "@/common/components/organisms/table/actionMenu";
import DataTable from "@/common/components/organisms/table/dataTable";
import { columnsType } from "@/common/models/model";
import { SearchBox } from "@/common/components/searchBox";
import { Content } from "@/common/components/templates/content/content";
import {
  billingTypeDetail,
  DefaultPageSize,
  filterSelector,
  errorCategoryMapping,
  errorStatusMapping,
} from "@/common/constants/constants";
import {
  Action,
  ComponentKeys,
  Entity,
  Filters,
  LookupEntityType,
} from "@/common/enums/enums";
import { getComponentValueSafe } from "@/common/utils/componentListHelper";
import { canAccess, isActive } from "@/common/utils/utils";
import { withContext } from "@/common/utils/withContext";
import { getBillingInError } from "@/services/invoices";
import { TransactionsErrorModel } from "@/services/models/session";
import store from "@/store";
import { setLoader, showNotification } from "@/store/app/reducer";
import { Close, Edit, Loop as LoopIcon } from "@mui/icons-material";
import { Grid, InputAdornment, Popover } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { CellStatus } from "@/common/components/organisms/table/dataTableCells";
import { EvesIconButton } from "@/common/components/atoms/iconButton/iconButton";
import { IBillingInErrorStateModel } from "../../shared/models/error";
import { getClientTimezone } from "@/common/utils/getClientTimezone";
import saveAs from "file-saver";
import { useSearchParams } from "react-router-dom";

const BillingInError = () => {
  const { t: translate } = useTranslation();
  const startDate = new Date(new Date().getFullYear(), 0, 1);
  startDate.setSeconds(0);
  const endDate = new Date();
  const [showfilter, setShowfilter] = useState<boolean>(false);
  endDate.setSeconds(0);
  endDate.setHours(23);
  endDate.setMinutes(59);
  const [searchParams] = useSearchParams();

  const statusParam = searchParams.get("Status");
  const errorTypeDescriptionParam = searchParams.get("ErrorTypeDescription");
  const [isPaymentDeclinedRedirect, setIsPaymentDeclinedRedirect] = useState(statusParam === "application" && errorTypeDescriptionParam === "payment_error");
  const [state, setStateData] = useState({
    connectorID: null,
    sortFields: "-timestamp",
    errorType: "",
    errorTypeDescription: errorTypeDescriptionParam || "",
    isErrorDetailModalOpen: false,
    isResetBtnDisabled: true,
    limit: DefaultPageSize,
    page: 0,
    projectFields: [],
    rows: [],
    search: "",
    selectedChargingStationID: "",
    selectedConnectorID: -1,
    totalRowsCount: 0,
    transactionErrorCode: "",
    siteID: "",
    showUserLookUpModal: false,
    canExport: true,
    canListSiteAreas: false,
    canListSites: false,
    isSiteLookupModalOpen: false,
    issuer: true,
    defaultIssuer: true,
    startDate: startDate,
    endDate: endDate,
    siteName: "",
    withSite: true,
    withCompany: true,
    canRead: false,
    withUser: true,
    canListUsers: false,
    isManualStatusChangePopupOpen: false,
    isRetryModalOpen: false,
    selectedRetryRowData: {},
    sessionID: "",
    selectedSiteId: "",
    selectedBillingData: null,
    status: statusParam || "",
  } as IBillingInErrorStateModel);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const defaultSortColumn = "-timestamp";
  let isOnClickSiteFilterClear = false;

  const siteLookupRef: any = useRef(null);
  const dataTableRef = useRef<any>();
  const isOrganizationActive = isActive(getComponentValueSafe(ComponentKeys.ORGANIZATION));
  const searchBoxRef = useRef<any>();

  const canUpdateBillingInError: boolean = canAccess(Entity.BILLING_INERROR, Action.UPDATE);

  const { refetch: fetchBillingInError } = useQuery(
    "BillingInError",
    () => {
      return getBillingInError(getBillingInErrorParams());
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      enabled: false,
      onSuccess: (data: any) => {
        if (data) {
          const canListSites = data?.canListSites;
          const canListSiteAreas = data?.canListSiteAreas;
          const billingData = data.result.map((d: any) => new TransactionsErrorModel(d));

          setStateData((currentData) => {
            return {
              ...currentData,
              rows: billingData,
              totalRowsCount: data.count,
              canListSiteAreas,
              canListSites,
              projectFields: data.projectFields && [...data.projectFields],
            };
          });
        }
      },
    },
  );

  const getBillingInErrorParams = (): any => {
    const params: any = {
      Limit: state.limit,
      Page: state.page + 1,
      SortFields: state.sortFields,
      WithSite: state.withSite,
      WithCompany: state.withCompany,
      WithUser: state.withUser,
    };

    if (state.search) params.Search = state.search;

    if (state.errorTypeDescription) {
      params.ErrorTypeDescription = state.errorTypeDescription;
    } else if (state.errorType) {
      params.ErrorType = state.errorType;
    }

    if (state.status) {
      params.Status = state.status;
    }

    if (state.siteID) {
      params.SiteID = state.siteID;
    }

    // ONLY skip date for payment declined redirect

    if (!isPaymentDeclinedRedirect) {
      params.StartDateTime = state.startDate;
      params.EndDateTime = state.endDate;
    }

    return params;
  };

  const columns: columnsType[] = [
    ...(canUpdateBillingInError
      ? [
        {
          id: "actions",
          label: `${translate("general.actions")}`,
          minWidth: 90,
          textAlign: "center",
          maxWidth: 100,
          projectField: "action",
          headerAlign: "left" as const,
        },
      ]
      : []),
    {
      id: "timestampDateFormated",
      label: "Date & Time",
      minWidth: 60,
      textAlign: "left",
      maxWidth: 150,
      allowSort: true,
      sortKey: "timestamp",
      projectField: "timestamp",
      headerAlign: "left" as const,
    },
    {
      id: "latestRetryAction",
      label: "Status",
      minWidth: 60,
      textAlign: "center",
      maxWidth: 150,
      allowSort: false,
      projectField: "latestRetryStatus",
    },
    {
      id: "id",
      label: "Session ID",
      minWidth: 60,
      textAlign: "center",
      maxWidth: 100,
      allowSort: false,
      projectField: "id",
    },
    {
      id: "notes",
      label: "Notes",
      minWidth: 60,
      textAlign: "center",
      maxWidth: 100,
      allowSort: false,
      projectField: "notes",
    },
    {
      id: "chargerID",
      label: "Charger ID",
      minWidth: 60,
      textAlign: "left",
      maxWidth: 200,
      allowSort: true,
      projectField: "chargerID",
    },
    {
      id: "chargeBoxID",
      label: "OCPP ID",
      minWidth: 60,
      textAlign: "left",
      maxWidth: 150,
      allowSort: false,
      projectField: "chargeBoxID",
      headerAlign: "left" as const,
    },
    {
      id: "errorCode",
      label: "Error Code",
      minWidth: 60,
      textAlign: "left",
      maxWidth: 180,
      projectField: "billingData.stop",
      headerAlign: "left" as const,
    },
    {
      id: "errorDescription",
      label: "Error Reason",
      minWidth: 80,
      allowSort: false,
      textAlign: "left",
      maxWidth: 150,
      enableEllipsis: true,
      projectField: "billingData.stop",
      headerAlign: "left" as const,
    },
    {
      id: "totalChargedAmount",
      label: "Charged Amount",
      minWidth: 60,
      textAlign: "left",
      maxWidth: 150,
      allowSort: false,
      projectField: "stop.price",
      headerAlign: "left" as const,
    },
    {
      id: "companyName",
      label: "Business Name",
      minWidth: 60,
      textAlign: "left",
      maxWidth: 150,
      allowSort: false,
      projectField: "company.name",
      headerAlign: "left" as const,
    },
    {
      id: "siteName",
      label: "Site Name",
      minWidth: 60,
      textAlign: "left",
      maxWidth: 150,
      allowSort: false,
      projectField: "site.name",
      headerAlign: "left" as const,
    },
  ];

  const onTableChange = (params: any) => {
    setStateData((currentData) => {
      return {
        ...currentData,
        sortFields: params.sortFields,
      };
    });
  };

  const onPageChange = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setStateData((currentData) => {
      return {
        ...currentData,
        page: newPage,
      };
    });
  };

  const onRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setStateData((currentData) => {
      return {
        ...currentData,
        page: 0,
        limit: parseInt(event.target.value),
      };
    });
  };

  const primaryActions: ITableActionMenuModel[] = canUpdateBillingInError
    ? [
      {
        title: () => `${translate("general.tooltips.retry")}`,
        icon: () => LoopIcon,
        iconColor: "primary",
        "data-cy": "billing-in-error-view-action",
        visible: (row: any) =>
          row.canUpdate &&
          !!row?.billingData?.stop?.statusDescription &&
          !["payment_error", "platform_error"].includes(
            row?.billingData?.stop?.statusDescription
          ),
        onClick: (row: any) => onClickRetry(row),
      },
      {
        title: () => `${translate("general.tooltips.manual_status_change")}`,
        icon: (row: any) => {
          return row.canUpdate ? Edit : null;
        },
        iconColor: "primary",
        "data-cy": "billing-in-error-manual-status-change-action",
        onClick: (row: any) => (row.canUpdate ? onClickManualStatusChange(row) : null),
      },
    ]
    : [];

  const onClickManualStatusChange = (row: any) => {
    setStateData((currentData) => {
      return {
        ...currentData,
        isManualStatusChangePopupOpen: true,
        sessionID: row.id,
      };
    });
  };

  const onCloseManualStatusChangePopup = () => {
    setStateData((currentData) => {
      return {
        ...currentData,
        isManualStatusChangePopupOpen: false,
        sessionID: "",
      };
    });
  };

  const onClickRetry = (row: any) => {
    const errorReason = billingTypeDetail(row?.billingData?.stop?.statusDescription);

    if (errorReason.action) {
      setStateData((currentData) => {
        return {
          ...currentData,
          isRetryModalOpen: true,
          selectedRetryRowData: errorReason,
          sessionID: row.id,
          selectedSiteId: row.siteID,
          selectedBillingData: row.billingData,
        };
      });
    }
  };

  const onCloseRetryModal = () => {
    setStateData((currentData) => {
      return {
        ...currentData,
        isRetryModalOpen: false,
        selectedRetryRowData: {},
        sessionID: "",
        selectedSiteId: "",
        selectedBillingData: null,
      };
    });
  };

  const onRetrySuccess = () => {
    // Refresh the data after successful retry
    loadBillingInErrorData();
  };

  const onManualStatusUpdateSuccess = () => {
    // Refresh the data after successful status update
    loadBillingInErrorData();
  };

  const SessionIDCell = (row: any) => {
    const sessionId = row?.id;
    const billingStatus = ["billed", "unbilled"].includes(row?.billingData?.stop?.status);
    const redirectUrl = billingStatus ? `#/invoices/in-transactions?TransactionID=${sessionId}` : `#/transactions/history?TransactionID=${sessionId}`;

    if (!sessionId) return <>-</>;
    return (
      <Grid
        data-cy="session-id-link"
        sx={{
          color: "#2469F3",
          cursor: "pointer",
          textDecoration: "underline",
        }}
        onClick={() => window.open(redirectUrl, "_blank")}
      >
        {sessionId}
      </Grid>
    );
  };

  const ErrorCodeDetailsCell = (row: any): JSX.Element => {
    return <span style={{ textTransform: "capitalize" }}>{row?.billingData?.stop?.statusDescription?.replace(/_/g, " ") || "-"}</span>;
  };

  const ErrorCodeCell = (row: any): JSX.Element => {
    const userErrorCode: string = row?.billingData?.stop?.status;
    const errorTitle = errorCategoryMapping[userErrorCode];

    return <p style={{ textTransform: "uppercase" }}>{errorTitle ?? "-"}</p>;
  };

  const InvoiceNotesCell = (row: any) => {
    return row?.latestRetryStatus?.action === "inprogress" && row?.latestRetryStatus.resolutionNote ? <NotesCell row={row} /> : <span>-</span>;
  };

  const ChargedAmount = (row: any) => {
    return <>${row?.paymentAmount ? (row?.paymentAmount / 100).toFixed(2) : row?.stop?.price?.toFixed(2)}</>;
  };

  const StatusCell = (row: any) => {
    const status = row?.latestRetryStatus?.action;
    const statusLabel = errorStatusMapping[status];

    return <CellStatus label={statusLabel || "-"} type={status} />;
  };

  const onFilterApplied = (searchText: string) => {
    setStateData((currentData) => {
      return {
        ...currentData,
        search: searchText,
        // Reset page to 0 when search text is changed
        page: searchText !== "" ? 0 : currentData.page,
        isResetBtnDisabled:
          searchText === "" &&
          currentData.siteName === "" &&
          !state.siteID &&
          !state.errorType &&
          (state.startDate.toString() == startDate.toISOString() || state.endDate.toString() == endDate.toISOString()),
      };
    });
  };

  const onErrorSelect = (selectedItems: string[]) => {
    const errorFilter: string = selectedItems?.length && selectedItems.length > 0 ? selectedItems.map((i: string) => filterSelector[Filters.BILLING_INERROR].value[i]).join("|") : "";

    setStateData((currentData) => {
      return {
        ...currentData,
        errorType: errorFilter,
        isResetBtnDisabled: false,
        page: errorFilter !== "" ? 0 : currentData.page,
      };
    });

    if (selectedItems.length === 0) {
      setStateData((currentData) => {
        return {
          ...currentData,
          isResetBtnDisabled: !state.search && !state.siteID && (state.startDate.toString() == startDate.toISOString() || state.endDate.toString() == endDate.toISOString()),
        };
      });
    }
  };

  const loadBillingInErrorData = async () => {
    store.dispatch(setLoader(true));
    try {
      await fetchBillingInError();
    } finally {
      store.dispatch(setLoader(false));
    }
  };

  useEffect(() => {
    loadBillingInErrorData();
  }, [state.limit, state.page, state.sortFields, state.errorType, state.search, state.siteID, state.startDate, state.endDate, isPaymentDeclinedRedirect]);

  const onDateRangeChange = (e: any) => {
    setStateData((currentData) => {
      return {
        ...currentData,
        startDate: e[0],
        endDate: e[1],
        page: e[0] && e[1] ? 0 : currentData.page,
        isResetBtnDisabled: false,
      };
    });
  };

  const ChargingStationCell = (row: any) => {
    if (!row.chargeBoxID) return <div style={{ textAlign: "center" }}>{"-"}</div>;
    return <>{row.chargeBoxID}</>;
  };

  const exportCSV = async () => {
    store.dispatch(setLoader(true));
    try {
      const { timeZone } = getClientTimezone();
      const params = {
        ...getBillingInErrorParams(),
        export: true,
        TimeZone: timeZone,
      };

      const response: any = await getBillingInError(params);
      const blob = new Blob([response]);
      saveAs(blob, "exported-billing-in-error.csv");

      store.dispatch(
        showNotification({
          showNotification: true,
          message: "Billing in error data exported successfully",
          type: "success",
        }),
      );
    } catch (error) {
      store.dispatch(
        showNotification({
          showNotification: true,
          message: "Failed to export data. Please try again.",
          type: "error",
        }),
      );
    } finally {
      store.dispatch(setLoader(false));
    }
  };

  const onClickResetFilters = () => {
    searchBoxRef?.current?.resetSearch();
    setStateData((currentData) => {
      return {
        ...currentData,
        search: "",
        isResetBtnDisabled: true,
        startDate: startDate,
        endDate: endDate,
        siteID: "",
        siteName: "",
        errorType: "",
        errorTypeDescription: "",
        status: "",
      };
    });
    setIsPaymentDeclinedRedirect(false);
  };

  const setSiteFilter = () => {
    if (!isOnClickSiteFilterClear) {
      setStateData((currentData) => {
        return {
          ...currentData,
          isSiteLookupModalOpen: true,
        };
      });
    }
    isOnClickSiteFilterClear = false;
  };

  const onClearSelectedSite = () => {
    isOnClickSiteFilterClear = true;
    siteLookupRef?.current?.setFocus();
    setStateData((currentData) => {
      return {
        ...currentData,
        siteID: "",
        siteName: "",
        isSiteLookupModalOpen: false,
        isResetBtnDisabled: !state.search,
      };
    });
  };

  const resetSiteFilter = () => {
    setStateData((currentData) => {
      return {
        ...currentData,
        isSiteLookupModalOpen: false,
      };
    });
  };

  const onSiteSelect = (sites: any) => {
    const siteName = sites?.length > 1 ? `${sites[0].name} (+${sites?.length - 1})` : sites?.length && sites[0].name;
    setStateData((currentData) => {
      return {
        ...currentData,
        isSiteLookupModalOpen: false,
        siteID: sites.map((site: any) => site.id).join("|"),
        siteName: siteName,
        isResetBtnDisabled: false,
      };
    });
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  return (
    <>
      <Content
        style={{ overflow: "auto" }}
        contentTop={
          <Grid container item display={"flex"} justifyContent={"space-between"}>
            <Grid item xs={8} lg={8} md={8} sm={6} xl={9}>
              {showfilter && (
                <Grid container item spacing={2} rowSpacing={1} sx={{ alignItems: "flex-end !important" }}>
                  {isOrganizationActive && (
                    <Grid item xs={12} sm={6} md={4} lg={3} xl={3}>
                      <DatetimeRangePicker
                        isDefaultRangeShortcut={true}
                        defaultStartDate={startDate}
                        defaultEndDate={endDate}
                        startDate={state.startDate}
                        endDate={state.endDate}
                        cleanable={false}
                        onDateRangeChange={onDateRangeChange}
                      />
                    </Grid>
                  )}

                  <Grid item xs={12} sm={6} md={4} lg={3} xl={3}>
                    <FilterSelector
                      label="Error Type"
                      filterOption={Filters.BILLING_INERROR}
                      defaultIssuer={state.defaultIssuer}
                      isResetBtnDisabled={state.isResetBtnDisabled}
                      onChange={onErrorSelect}
                      data-cy="error-type-filter"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={3} xl={3}>
                    <EvesTextbox
                      value={state.siteName}
                      id="site-area-standard-search"
                      label={`${translate("sites.titles")}`}
                      type="text"
                      variant="standard"
                      onClick={setSiteFilter}
                      onChange={onFilterApplied}
                      ref={siteLookupRef}
                      isFilter={true}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <EvesIconButton icon={Close} fontSize={15} onClick={onClearSelectedSite}></EvesIconButton>
                          </InputAdornment>
                        ),
                        readOnly: true,
                      }}
                    />
                  </Grid>

                  <Grid data-cy="search-field" item xs={12} sm={6} md={4} lg={3} xl={3}>
                    <SearchBox ref={searchBoxRef} onChange={onFilterApplied} onRemoveClick={fetchBillingInError} isTransaction={true} handleClickStart={handleClick} />
                  </Grid>
                </Grid>
              )}
            </Grid>
            <Grid item gap={"5px"} mt={1} xs={4} sm={6} lg={4} xl={3} md={4} sx={{ flexWrap: "wrap" }}>
              <ButtonGroup
                {...{
                  showfilter,
                  setShowfilter,
                  onClickExport: exportCSV,
                  isExport: true,
                  state,
                  showRefresh: true,
                  hideLabel: false,
                  resetFilters: onClickResetFilters,
                  onRefresh: () => {
                    fetchBillingInError();
                  },
                }}
              />
            </Grid>
            <Popover
              id={id}
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              style={{ marginTop: "20px", marginLeft: "160px" }}
            >
              <div
                style={{
                  padding: "10px",
                  maxWidth: "370px",
                  position: "relative",
                }}
              >
                <p style={{ fontWeight: 500, fontSize: "12px" }}>Search Criteria : Session ID, Charger ID, OCPP ID</p>
              </div>
            </Popover>
          </Grid>
        }
        contentBody={
          <DataTable
            ref={dataTableRef}
            className="billing-in-error-table"
            rows={state.rows}
            columns={columns}
            totalRowsCount={state.totalRowsCount}
            {...(canUpdateBillingInError ? { actionsMenu: primaryActions } : {})}
            defaultSortField={defaultSortColumn}
            defaultSortDescending={true}
            projectFields={state.projectFields}
            data-cy="billing-in-error-table"
            onChange={onTableChange}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            count={state.totalRowsCount}
            limit={state.limit}
            page={state.page}
            tablePagination={true}
          >
            <SessionIDCell slot="id" />
            <StatusCell slot="latestRetryAction" />
            <ErrorCodeCell slot="errorCode" />
            <ErrorCodeDetailsCell slot="errorDescription" />
            <ChargingStationCell slot="chargeBoxID" />
            <ChargedAmount slot="totalChargedAmount" />
            <InvoiceNotesCell slot="notes" />
          </DataTable>
        }
      ></Content>
      {state.isManualStatusChangePopupOpen && (
        <ErrorDetailModal
          isErrorDetailModalOpen={state.isManualStatusChangePopupOpen}
          onClose={onCloseManualStatusChangePopup}
          onUpdateSuccess={onManualStatusUpdateSuccess}
          sessionID={state.sessionID || ""}
        />
      )}
      {state.isRetryModalOpen && (
        <RetryModal
          onClose={onCloseRetryModal}
          isRetryModalOpen={state.isRetryModalOpen}
          selectedRowData={state.selectedRetryRowData}
          sessionID={state.sessionID || ""}
          selectedSiteId={state.selectedSiteId || ""}
          onRetrySuccess={onRetrySuccess}
          selectedBillingData={state.selectedBillingData || {}}
        />
      )}
      {state.isSiteLookupModalOpen && (
        <LookupComponent
          title={`${translate("sites.select_sites")}`}
          showLookupModal={state.isSiteLookupModalOpen}
          onModalClose={resetSiteFilter}
          selectionMode="multiple"
          entityType={LookupEntityType.SITE}
          params={[{ key: "Issuer", value: state.issuer }]}
          onSelect={onSiteSelect}
          defaultSortColumn="name"
        />
      )}
    </>
  );
};

export default withContext(BillingInError);
